// File: src/components/pages/payments/ImportStatement.jsx
// "Import Statement" tab under Manage Payments.
//
// Workflow:
//   1. IMPORT  — instructor selects a bank-statement PDF; backend extracts
//                CREDIT entries and maps references against active students.
//   2. REVIEW  — unmatched entries get a student dropdown; every entry gets a
//                Classes dropdown (1–5, pre-selected when the amount divides
//                cleanly by hourly rate × lesson duration); entries can be removed.
//   3. SAVE    — persists the statement (StudentPaymentsBankStmt) and every
//                entry (StudentPaymentsBankTransactions, Status=Saved). The
//                instructor can leave and resume mapping later — saved entries
//                reload automatically when the tab opens.
//   4. PROCESS — enabled only once entries are saved. All ReadyToProcess=Yes
//                rows become Confirmed StudentPayments (via the existing
//                approvePayment path, which also updates student allowances)
//                and the bank transactions are marked Processed.

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import {
  Upload, FileText, Trash2, CheckCircle2, AlertCircle,
  RefreshCw, X, HelpCircle, Save, PlayCircle, FileDown
} from 'lucide-react';
import { paymentImportAPI } from '../../../services/api';
import '../../../styles/unified.css';

const fmtDate   = (d) => !d ? '—' : new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
const fmtAmount = (n) => typeof n === 'number' ? `£${n.toFixed(2)}` : '—';

const ImportStatement = () => {
  const { getAccessTokenSilently } = useAuth0();
  const fileInputRef = useRef(null);

  // mode: 'idle' (nothing yet) | 'parsed' (imported, unsaved) | 'saved' (persisted)
  const [mode,       setMode]       = useState('idle');
  const [file,       setFile]       = useState(null);
  const [loading,    setLoading]    = useState(true);    // initial saved-txn check
  const [importing,  setImporting]  = useState(false);
  const [saving,     setSaving]     = useState(false);
  const [processing, setProcessing] = useState(false);
  const [error,      setError]      = useState(null);
  const [notice,     setNotice]     = useState(null);    // success banners

  const [entries,    setEntries]    = useState([]);
  const [students,   setStudents]   = useState([]);
  const [config,     setConfig]     = useState(null);    // { lessonDuration, hourlyRateAutomatic, hourlyRateManual }
  const [duplicateWarning, setDuplicateWarning] = useState(null); // set when Import detects an already-saved statement
  const [statements, setStatements] = useState({});      // statementId → meta (saved mode)
  const [selectedStatementId, setSelectedStatementId] = useState(null); // saved mode: statement in view
  const [dirtyIds,   setDirtyIds]   = useState(new Set()); // edited saved rows pending "Save Changes"
  const [deletingIds, setDeletingIds] = useState(new Set()); // saved rows with a delete request in flight

  // ── Mirror of the backend / PaymentOptionsForm rule ────────────────────────
  // cost per class = hourly rate (by preferredBookingType) × lessonDuration.
  const suggestClasses = (amount, student, cfg) => {
    if (!cfg || !amount) return null;
    const rate = (student && student.preferredBookingType === 'Automatic')
      ? cfg.hourlyRateAutomatic
      : cfg.hourlyRateManual;
    const costPerClass = rate * (cfg.lessonDuration || 2);
    if (!costPerClass) return null;
    const raw = amount / costPerClass;
    const rounded = Math.round(raw);
    return (Math.abs(raw - rounded) < 0.01 && rounded >= 1 && rounded <= 5) ? rounded : null;
  };

  // ── Resume: load saved (unprocessed) transactions when the tab opens ───────
  const loadSaved = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Transactions response also carries active students + pricing config,
      // so dropdowns and class suggestions work without a fresh parse.
      const txnRes = await paymentImportAPI.getSavedTransactions(getAccessTokenSilently);
      const data = txnRes.data || {};
      const txns = data.transactions || [];
      if (txns.length > 0) {
        setEntries(txns.map(t => ({
          entryId: t.paymentId,           // saved rows are keyed by their real paymentId
          paymentId: t.paymentId,
          statementId: t.statementId,
          date: t.date,
          reference: t.reference,
          amount: t.amount,
          matched: !!t.studentId,
          matchedStudentId: t.studentId,
          matchedStudentName: t.studentName,
          classes: t.numberOfClasses,
          readyToProcess: t.readyToProcess
        })));
        const stmts = data.statements || {};
        setStatements(stmts);
        setStudents(data.students || []);
        setConfig(data.config || null);
        // Keep the current selection if that statement still has saved rows;
        // otherwise default to the first (oldest) statement.
        setSelectedStatementId(prev => {
          const ids = [...new Set(txns.map(t => t.statementId))];
          return ids.includes(prev) ? prev : ids[0] || null;
        });
        setMode('saved');
      } else {
        setMode('idle');
      }
    } catch (e) {
      setError(e.message || 'Failed to load saved transactions');
      setMode('idle');
    } finally {
      setLoading(false);
    }
  }, [getAccessTokenSilently]);

  useEffect(() => { loadSaved(); }, [loadSaved]);

  // ── File selection ─────────────────────────────────────────────────────────
  const handleFileChange = (e) => {
    const selected = e.target.files?.[0];
    setError(null);
    setDuplicateWarning(null);   // a newly selected file hasn't been checked yet
    if (!selected) { setFile(null); return; }
    if (selected.type !== 'application/pdf') {
      setError('Please select a PDF file.');
      setFile(null);
      return;
    }
    setFile(selected);
  };

  const clearFile = () => {
    setFile(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // ── Import (parse) ─────────────────────────────────────────────────────────
  const handleImport = async () => {
    if (!file) return;
    setImporting(true);
    setError(null);
    setNotice(null);
    setDuplicateWarning(null);
    try {
      const res  = await paymentImportAPI.parseStatement(file, getAccessTokenSilently);
      const data = res.data || res;
      setEntries(data.entries || []);
      setStudents(data.students || []);
      setConfig(data.config || null);
      setStatements({});
      setDirtyIds(new Set());
      setMode('parsed');
      if (data.duplicate?.isDuplicate) {
        setDuplicateWarning(data.duplicate);
      }
    } catch (e) {
      setError(e.message || 'Failed to import statement');
    } finally {
      setImporting(false);
    }
  };

  const handleReset = () => {
    setEntries([]);
    setStatements({});
    setDirtyIds(new Set());
    clearFile();
    setError(null);
    setNotice(null);
    setDuplicateWarning(null);
    // Re-check for saved rows rather than assuming idle
    loadSaved();
  };

  // ── Entry edits (both modes) ───────────────────────────────────────────────
  const markDirty = (entryId) => {
    if (mode !== 'saved') return;
    setDirtyIds(prev => new Set(prev).add(entryId));
  };

  const handleStudentSelect = (entryId, studentId) => {
    setEntries(prev => prev.map(e => {
      if (e.entryId !== entryId) return e;
      const student = students.find(s => s.studentId === studentId) || null;
      const suggested = student ? suggestClasses(e.amount, student, config) : null;
      return {
        ...e,
        matchedStudentId:   studentId || null,
        matchedStudentName: student ? student.fullName : null,
        classes: suggested ?? e.classes ?? null
      };
    }));
    markDirty(entryId);
  };

  const handleClassesSelect = (entryId, value) => {
    setEntries(prev => prev.map(e =>
      e.entryId === entryId
        ? { ...e, classes: value ? parseInt(value, 10) : null }
        : e
    ));
    markDirty(entryId);
  };

  const handleRemoveEntry = async (entry) => {
    if (mode === 'parsed') {
      // Not yet saved — nothing to delete server-side, just drop it locally.
      setEntries(prev => prev.filter(e => e.entryId !== entry.entryId));
      return;
    }

    // Saved mode: this is a real StudentPaymentsBankTransactions row.
    // Deleting it is permanent, so confirm first.
    if (!window.confirm(
      `Delete this credit entry (${fmtAmount(entry.amount)} on ${fmtDate(entry.date)})? ` +
      `This permanently removes it — it will not be saved or processed.`
    )) {
      return;
    }

    setError(null);
    setDeletingIds(prev => new Set(prev).add(entry.entryId));
    try {
      await paymentImportAPI.deleteTransaction(entry.paymentId, getAccessTokenSilently);
      setEntries(prev => prev.filter(e => e.entryId !== entry.entryId));
      setDirtyIds(prev => {
        const next = new Set(prev);
        next.delete(entry.entryId);
        return next;
      });
      setNotice('Entry deleted.');
    } catch (e) {
      setError(e.message || 'Failed to delete entry');
    } finally {
      setDeletingIds(prev => {
        const next = new Set(prev);
        next.delete(entry.entryId);
        return next;
      });
    }
  };

  // ── Save ───────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    if (mode === 'parsed' && duplicateWarning) {
      setError('This statement has already been imported — cannot save a duplicate.');
      return;
    }
    setSaving(true);
    setError(null);
    setNotice(null);
    try {
      if (mode === 'parsed') {
        // First save: statement + all entries
        const payload = entries.map(e => ({
          date: e.date,
          reference: e.reference,
          amount: e.amount,
          studentId: e.matchedStudentId || null,
          studentName: e.matchedStudentName || null,
          classes: e.classes || null
        }));
        await paymentImportAPI.saveStatement(file, payload, getAccessTokenSilently);
        setNotice('Statement and credit entries saved. You can update mappings any time before processing.');
        clearFile();
        await loadSaved();   // reload as saved rows (with real paymentIds)
      } else if (mode === 'saved' && dirtyIds.size > 0) {
        // Subsequent save: push only edited rows
        const updates = entries
          .filter(e => dirtyIds.has(e.entryId))
          .map(e => ({
            paymentId: e.paymentId,
            studentId: e.matchedStudentId || null,
            studentName: e.matchedStudentName || null,
            numberOfClasses: e.classes || null
          }));
        await paymentImportAPI.updateTransactions(updates, getAccessTokenSilently);
        setNotice('Changes saved.');
        setDirtyIds(new Set());
        await loadSaved();
      }
    } catch (e) {
      setError(e.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  // ── Per-statement scoping (saved mode shows one statement at a time) ───────
  const visibleEntries = mode === 'saved'
    ? entries.filter(e => e.statementId === selectedStatementId)
    : entries;
  const statementIds = mode === 'saved'
    ? [...new Set(entries.map(e => e.statementId))]
    : [];

  // ── Process ────────────────────────────────────────────────────────────────
  const readyCount = visibleEntries.filter(e => e.matchedStudentId && e.classes).length;

  const handleProcess = async () => {
    if (mode !== 'saved' || !selectedStatementId) return;
    if (dirtyIds.size > 0) {
      setError('You have unsaved changes — click Save before processing.');
      return;
    }
    const notReady = visibleEntries.length - readyCount;
    const confirmMsg = notReady > 0
      ? `Process ${readyCount} ready ${readyCount === 1 ? 'entry' : 'entries'}? ` +
        `${notReady} without a student or class count will remain saved for later.`
      : `Process all ${readyCount} entries? Payments will be created and confirmed, and student allowances updated.`;
    if (!window.confirm(confirmMsg)) return;

    setProcessing(true);
    setError(null);
    setNotice(null);
    try {
      const res = await paymentImportAPI.processTransactions(selectedStatementId, getAccessTokenSilently);
      const { summary, details } = res.data || {};
      const parts = [`${summary?.processed ?? 0} payment${(summary?.processed ?? 0) === 1 ? '' : 's'} created and confirmed`];
      if (summary?.skippedNotReady > 0) parts.push(`${summary.skippedNotReady} left saved (not ready)`);
      if (summary?.failed > 0) parts.push(`${summary.failed} FAILED`);
      setNotice(`Processing complete: ${parts.join(', ')}.`);
      if (summary?.failed > 0 && details?.failed?.length) {
        setError(`Failed: ${details.failed.map(f => `${f.studentName || f.paymentId} (${f.error})`).join('; ')}`);
      }
      await loadSaved();   // processed rows drop out of the Saved list
    } catch (e) {
      setError(e.message || 'Failed to process transactions');
    } finally {
      setProcessing(false);
    }
  };

  // ── Derived display state ──────────────────────────────────────────────────
  const busy = importing || saving || processing;
  const totalAmount  = visibleEntries.reduce((s, e) => s + (e.amount || 0), 0);
  const selectedStmt = selectedStatementId ? statements[selectedStatementId] : null;

  const saveLabel =
    duplicateWarning ? 'Already Imported' :
    mode === 'parsed' ? 'Save' :
    dirtyIds.size > 0 ? `Save Changes (${dirtyIds.size})` : 'Save';
  const saveDisabled = busy || entries.length === 0 ||
    (mode === 'saved' && dirtyIds.size === 0) || mode === 'idle' ||
    (mode === 'parsed' && !!duplicateWarning);
  const processDisabled = busy || mode !== 'saved' || readyCount === 0 || dirtyIds.size > 0;

  // ── View the stored statement PDF (presigned S3 URL) ───────────────────────
  const handleViewPdf = async () => {
    if (!selectedStatementId) return;
    try {
      const res = await paymentImportAPI.getStatementDownloadUrl(selectedStatementId, getAccessTokenSilently);
      const url = res.data?.url;
      if (url) window.open(url, '_blank', 'noopener');
    } catch (e) {
      setError(e.message || 'Failed to open statement PDF');
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>

      {/* Upload card */}
      <div className="card">
        <div className="card-body" style={{ padding: '1rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
            <FileText size={18} color="#667eea" />
            <label style={{ fontWeight: 600, fontSize: '0.95rem' }}>Bank Statement (PDF)</label>

            <input
              ref={fileInputRef}
              type="file"
              accept="application/pdf,.pdf"
              onChange={handleFileChange}
              style={{ display: 'none' }}
            />

            <button
              onClick={() => fileInputRef.current?.click()}
              className="btn-secondary"
              disabled={busy}
            >
              <Upload size={14} style={{ marginRight: '0.35rem', verticalAlign: '-2px' }} />
              {file ? 'Change File' : 'Select File'}
            </button>

            {file && (
              <span style={{
                display: 'inline-flex', alignItems: 'center', gap: '0.4rem',
                padding: '0.3rem 0.6rem', background: '#eef2ff',
                border: '1px solid #c7d2fe', borderRadius: '0.4rem',
                fontSize: '0.85rem', color: '#3730a3'
              }}>
                {file.name}
                <X size={13} style={{ cursor: 'pointer' }} onClick={clearFile} title="Remove file" />
              </span>
            )}

            <div style={{ marginLeft: 'auto', display: 'flex', gap: '0.5rem' }}>
              {mode !== 'idle' && (
                <button onClick={handleReset} className="btn-secondary" disabled={busy}>
                  <RefreshCw size={14} style={{ marginRight: '0.35rem', verticalAlign: '-2px' }} />
                  Refresh
                </button>
              )}
              <button
                onClick={handleImport}
                disabled={!file || busy}
                className="btn-primary"
              >
                {importing ? 'Importing…' : 'Import'}
              </button>
            </div>
          </div>

          <p style={{ margin: '0.6rem 0 0', fontSize: '0.8rem', color: '#9ca3af' }}>
            Credit (money-in) entries are extracted and matched against your active students.
            Save keeps the statement and entries so you can finish mapping later;
            Process turns every fully mapped entry into a confirmed payment.
          </p>
        </div>
      </div>

      {/* Saved-statement selector (saved mode) — one statement in view at a time.
          Fully processed statements no longer appear (only Saved rows load). */}
      {mode === 'saved' && statementIds.length > 0 && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: '0.6rem', flexWrap: 'wrap',
          padding: '0.6rem 1rem', background: '#eef2ff', border: '1px solid #c7d2fe',
          borderRadius: '0.5rem', fontSize: '0.85rem', color: '#3730a3'
        }}>
          <span style={{ fontWeight: 600 }}>Saved statement:</span>
          {statementIds.length > 1 ? (
            <select
              value={selectedStatementId || ''}
              onChange={e => { setSelectedStatementId(e.target.value); setNotice(null); setError(null); }}
              className="form-select"
              disabled={busy}
              style={{ fontSize: '0.85rem', padding: '0.3rem 0.5rem', maxWidth: '420px' }}
            >
              {statementIds.map(id => {
                const s = statements[id];
                const count = entries.filter(e => e.statementId === id).length;
                return (
                  <option key={id} value={id}>
                    {s ? `${s.statementName} — ${fmtDate(s.statementDate)}` : id} ({count} pending)
                  </option>
                );
              })}
            </select>
          ) : (
            <span>
              {selectedStmt ? `${selectedStmt.statementName} (${fmtDate(selectedStmt.statementDate)})` : selectedStatementId}
            </span>
          )}
          <button
            onClick={handleViewPdf}
            className="btn-secondary"
            disabled={busy}
            title="Open the stored statement PDF"
            style={{ marginLeft: 'auto', fontSize: '0.8rem', padding: '0.3rem 0.6rem' }}
          >
            <FileDown size={13} style={{ marginRight: '0.3rem', verticalAlign: '-2px' }} />
            View PDF
          </button>
        </div>
      )}

      {/* Duplicate-statement warning — set right after Import, before any
          mapping effort is spent on a statement that's already on file */}
      {duplicateWarning && (
        <div style={{
          display: 'flex', alignItems: 'flex-start', gap: '0.6rem',
          padding: '0.75rem 1rem', background: '#fff3cd', border: '1px solid #ffc107',
          borderRadius: '0.5rem', color: '#856404', fontSize: '0.88rem'
        }}>
          <AlertCircle size={18} style={{ flexShrink: 0, marginTop: '0.1rem' }} />
          <span>
            <strong>This statement has already been imported.</strong>{' '}
            "{duplicateWarning.statementName}"
            {duplicateWarning.statementDate ? ` (dated ${fmtDate(duplicateWarning.statementDate)})` : ''}{' '}
            is already saved — Save is disabled to prevent a duplicate. Open it from your saved
            statements instead, or select a different file to import.
          </span>
        </div>
      )}

      {notice && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: '0.5rem',
          padding: '0.7rem 1rem', background: '#d1e7dd', border: '1px solid #198754',
          borderRadius: '0.5rem', color: '#0a3622', fontSize: '0.88rem'
        }}>
          <CheckCircle2 size={17} /><span>{notice}</span>
        </div>
      )}

      {error && (
        <div className="booking-error-banner">
          <AlertCircle size={18} /><span>{error}</span>
        </div>
      )}

      {/* Loading */}
      {(loading || importing) && (
        <div className="loading-state"><div className="spinner" /></div>
      )}

      {/* Summary bar */}
      {!loading && !importing && visibleEntries.length > 0 && (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
          gap: '0.75rem'
        }}>
          <div style={{ padding: '0.75rem 1rem', background: '#eef2ff', border: '1px solid #c7d2fe', borderRadius: '0.5rem', textAlign: 'center' }}>
            <div style={{ fontSize: '1.4rem', fontWeight: 700, color: '#3730a3' }}>{visibleEntries.length}</div>
            <div style={{ fontSize: '0.78rem', color: '#3730a3', fontWeight: 500 }}>Credit Entries</div>
          </div>
          <div style={{ padding: '0.75rem 1rem', background: '#d1e7dd', border: '1px solid #198754', borderRadius: '0.5rem', textAlign: 'center' }}>
            <div style={{ fontSize: '1.4rem', fontWeight: 700, color: '#0a3622' }}>{readyCount}</div>
            <div style={{ fontSize: '0.78rem', color: '#0a3622', fontWeight: 500 }}>Ready to Process</div>
          </div>
          <div style={{ padding: '0.75rem 1rem', background: '#fff3cd', border: '1px solid #ffc107', borderRadius: '0.5rem', textAlign: 'center' }}>
            <div style={{ fontSize: '1.4rem', fontWeight: 700, color: '#856404' }}>{visibleEntries.length - readyCount}</div>
            <div style={{ fontSize: '0.78rem', color: '#856404', fontWeight: 500 }}>Needs Attention</div>
          </div>
          <div style={{ padding: '0.75rem 1rem', background: '#f0fdf4', border: '1px solid #86efac', borderRadius: '0.5rem', textAlign: 'center' }}>
            <div style={{ fontSize: '1.4rem', fontWeight: 700, color: '#15803d' }}>{fmtAmount(totalAmount)}</div>
            <div style={{ fontSize: '0.78rem', color: '#15803d', fontWeight: 500 }}>Total Credited</div>
          </div>
        </div>
      )}

      {/* Empty states */}
      {!loading && !importing && visibleEntries.length === 0 && mode === 'parsed' && (
        <div style={{ textAlign: 'center', padding: '3rem', color: '#9ca3af', fontSize: '0.9rem' }}>
          No credit entries were found in this statement.
        </div>
      )}

      {/* Credit list */}
      {!loading && !importing && visibleEntries.length > 0 && (
        <div className="card" style={{ overflow: 'hidden' }}>
          {/* Table header */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1.9fr 0.7fr 1.4fr 0.9fr 3.5rem',
            gap: '0.5rem',
            padding: '0.75rem 1rem',
            background: '#f9fafb',
            borderBottom: '1px solid #e5e7eb',
            fontSize: '0.78rem',
            fontWeight: 600,
            color: '#6b7280',
            textTransform: 'uppercase',
            letterSpacing: '0.04em'
          }}>
            <span>Date</span>
            <span>Reference</span>
            <span>Amount</span>
            <span>Student</span>
            <span>Classes</span>
            <span />
          </div>

          {/* Rows */}
          {visibleEntries.map(entry => {
            const nowMapped = !!entry.matchedStudentId;
            const isReady = nowMapped && !!entry.classes;
            const isDirty = dirtyIds.has(entry.entryId);

            return (
              <div
                key={entry.entryId}
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1.9fr 0.7fr 1.4fr 0.9fr 3.5rem',
                  gap: '0.5rem',
                  alignItems: 'center',
                  padding: '0.65rem 1rem',
                  borderBottom: '1px solid #f3f4f6',
                  fontSize: '0.88rem',
                  background: isDirty ? '#eef2ff' : (isReady ? 'transparent' : '#fffbeb')
                }}
              >
                <span style={{ color: '#374151' }}>{fmtDate(entry.date)}</span>

                <span style={{ color: '#374151', wordBreak: 'break-word' }} title={entry.reference}>
                  {entry.reference || <em style={{ color: '#9ca3af' }}>No reference</em>}
                </span>

                <span style={{ fontWeight: 600, color: '#15803d' }}>{fmtAmount(entry.amount)}</span>

                {/* Student cell: parse-time exact matches render as fixed text;
                    everything else (including saved rows) stays selectable so
                    the instructor can update the mapping before processing */}
                {entry.matched && mode === 'parsed' ? (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem', color: '#0a3622', fontWeight: 500 }}>
                    <CheckCircle2 size={15} color="#198754" />
                    {entry.matchedStudentName}
                  </span>
                ) : (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem' }}>
                    {nowMapped
                      ? <CheckCircle2 size={15} color="#198754" />
                      : <HelpCircle size={15} color="#f59e0b" />}
                    <select
                      value={entry.matchedStudentId || ''}
                      onChange={e => handleStudentSelect(entry.entryId, e.target.value)}
                      className="form-select"
                      disabled={busy}
                      style={{ minWidth: '150px', fontSize: '0.85rem', padding: '0.3rem 0.5rem' }}
                    >
                      <option value="">— Select student —</option>
                      {students.map(s => (
                        <option key={s.studentId} value={s.studentId}>{s.fullName}</option>
                      ))}
                    </select>
                  </span>
                )}

                {/* Classes cell */}
                <select
                  value={entry.classes || ''}
                  onChange={e => handleClassesSelect(entry.entryId, e.target.value)}
                  className="form-select"
                  disabled={busy}
                  title={entry.classes
                    ? `${entry.classes} class${entry.classes > 1 ? 'es' : ''} based on lesson duration × hourly rate`
                    : 'Amount does not match the configured class cost — select the number of classes'}
                  style={{
                    fontSize: '0.85rem',
                    padding: '0.3rem 0.5rem',
                    borderColor: entry.classes ? undefined : '#f59e0b',
                    background: entry.classes ? undefined : '#fffbeb'
                  }}
                >
                  <option value="">—</option>
                  {[1, 2, 3, 4, 5].map(n => (
                    <option key={n} value={n}>{n}</option>
                  ))}
                </select>

                {/* Delete: pre-save rows just drop locally; saved rows are a
                    real StudentPaymentsBankTransactions record and are
                    deleted server-side. A small ready indicator is kept
                    alongside it in saved mode since the textual badge is gone. */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', justifyContent: 'center' }}>
                  {mode === 'saved' && (
                    isReady
                      ? <CheckCircle2 size={14} color="#198754" title="Ready to process" />
                      : <span style={{ fontSize: '0.7rem', color: '#f59e0b', fontWeight: 600 }} title="Needs student and classes">—</span>
                  )}
                  <button
                    onClick={() => handleRemoveEntry(entry)}
                    className="btn-icon"
                    title={mode === 'saved' ? 'Delete this saved entry permanently' : 'Remove entry'}
                    disabled={busy || deletingIds.has(entry.entryId)}
                    style={{
                      background: 'none', border: 'none',
                      cursor: (busy || deletingIds.has(entry.entryId)) ? 'default' : 'pointer',
                      color: '#dc3545', padding: '0.3rem', borderRadius: '0.35rem',
                      display: 'inline-flex', justifyContent: 'center',
                      opacity: deletingIds.has(entry.entryId) ? 0.5 : 1
                    }}
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            );
          })}

          {/* Action bar: Save / Process */}
          <div style={{
            display: 'flex', alignItems: 'center', gap: '0.75rem',
            padding: '0.85rem 1rem', background: '#f9fafb', borderTop: '1px solid #e5e7eb'
          }}>
            <span style={{ fontSize: '0.82rem', color: '#6b7280' }}>
              {duplicateWarning
                ? 'This statement is already saved — see the warning above.'
                : mode === 'parsed'
                  ? 'Save to keep these entries — you can finish mapping later.'
                  : `${readyCount} of ${visibleEntries.length} ${visibleEntries.length === 1 ? 'entry' : 'entries'} in this statement ready to process.`}
            </span>
            <div style={{ marginLeft: 'auto', display: 'flex', gap: '0.5rem' }}>
              <button
                onClick={handleSave}
                disabled={saveDisabled}
                className="btn-secondary"
                title={duplicateWarning ? 'This exact statement has already been imported' : undefined}
              >
                <Save size={14} style={{ marginRight: '0.35rem', verticalAlign: '-2px' }} />
                {saving ? 'Saving…' : saveLabel}
              </button>
              <button
                onClick={handleProcess}
                disabled={processDisabled}
                className="btn-primary"
                title={mode !== 'saved'
                  ? 'Save the entries first'
                  : dirtyIds.size > 0
                    ? 'Save your changes first'
                    : readyCount === 0
                      ? 'No entries are ready to process'
                      : `Process ${readyCount} ready ${readyCount === 1 ? 'entry' : 'entries'}`}
              >
                <PlayCircle size={14} style={{ marginRight: '0.35rem', verticalAlign: '-2px' }} />
                {processing ? 'Processing…' : 'Process'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ImportStatement;
